import { adicionarLogo } from "../../services/pdf-logo";
// components/recibo-manutencao.tsx
import { useEffect, useState, useRef } from "react";
import jsPDF from "jspdf";
import api from "../../services/api";
import { useAppSelector } from "../../store/hooks";
import { formatDate, formatarParaReais } from "../../services/utilitarios";
import { toast } from "react-toastify";
import { obterIdTenent, obterNomeUsuario } from "../../services/seguranca";

interface VeiculoInfo {
  placa: string;
  marca: string;
  modelo: string;
  ano: string;
}

interface PrestadorInfo {
  nome: string;
  contato?: string;
}

interface ManutencaoCompleta {
  id?: string;
  hodometro: number;
  descricao: string;
  dataRealizado: Date;
  veiculo?: VeiculoInfo;
  prestadorServico?: PrestadorInfo;
  servicoVeiculoServicos?: any[];
  servicoVeiculoPecas?: any[];
  imagem?: string;
}

interface ReciboManutencaoProps {
  manutencao: ManutencaoCompleta;
  trigger: number;
}

export const ReciboManutencao: React.FC<ReciboManutencaoProps> = ({
  manutencao,
  trigger,
}) => {
  const token = useAppSelector((state) => state.token);
  const [carregando, setCarregando] = useState(false);
  const [gerandoPDF, setGerandoPDF] = useState(false);
  
  // Refs para controle de estado
  const ultimoTriggerRef = useRef(0);
  const pdfEmExecucaoRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const garantirString = (valor: any): string => {
    if (valor === null || valor === undefined) return 'N/A';
    return String(valor);
  };

  const obterLogoDaEmpresa = async (): Promise<string> => {
    try {
      const response = await api.get(
        `Empresa/ObterURLLogo/${obterIdTenent(token)}`
      );
      return response.data;
    } catch (error) {
      console.error("Erro ao obter logo:", error);
      return "/images/s.png";
    }
  };

  const carregarLogoBase64 = async (): Promise<string | null> => {
    try {
      const logoUrl = await obterLogoDaEmpresa();

      let urlFinal = logoUrl;
      if (logoUrl.startsWith('/')) {
        urlFinal = `${window.location.origin}${logoUrl}`;
      }

      const response = await fetch(urlFinal);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          const base64 = reader.result as string;
          resolve(base64);
        };

        reader.onerror = () => {
          console.error("Erro ao converter logo para base64");
          reject(new Error("Erro ao converter logo"));
        };

        reader.readAsDataURL(blob);
      });

    } catch (error) {
      console.error("Erro ao carregar logo:", error);
      return null;
    }
  };

  const carregarImagens = async (): Promise<{nome: string, url: string, carregada: boolean}[]> => {
    if (!manutencao) return [];

    const imagens: {nome: string, url: string, carregada: boolean}[] = [];

    // Carregar imagem principal da manutenção
    if (manutencao.imagem && manutencao.imagem.trim() !== '') {
      try {
        let urlFinal = manutencao.imagem;
        if (manutencao.imagem.startsWith('/')) {
          urlFinal = `${window.location.origin}${manutencao.imagem}`;
        }

        const response = await fetch(urlFinal, {
          method: 'GET',
          mode: 'cors'
        });

        if (response.ok) {
          const blob = await response.blob();
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error("Erro ao ler imagem"));
            reader.readAsDataURL(blob);
          });

          imagens.push({
            nome: "Imagem da Manutenção",
            url: base64,
            carregada: true
          });

        } else {
          console.warn("Imagem principal não acessível:", manutencao.imagem);
          imagens.push({
            nome: "Imagem da Manutenção",
            url: manutencao.imagem,
            carregada: false
          });
        }
      } catch (err: any) {
        console.warn(`Erro ao carregar imagem principal:`, err);
        imagens.push({
          nome: "Imagem da Manutenção",
          url: manutencao.imagem,
          carregada: false
        });
      }
    }

    return imagens;
  };

  const adicionarImagensAoPDF = async (
    pdf: jsPDF, 
    posY: number, 
    dimensoes: any, 
    paleta: any, 
    imagensCarregadasData: any[]
  ): Promise<number> => {
    const imagensValidas = imagensCarregadasData.filter(img => img.carregada);
    if (imagensValidas.length === 0) {
      return posY;
    }

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(paleta.primaria);
    pdf.text("REGISTROS FOTOGRÁFICOS", dimensoes.margem, posY);
    posY += 8;

    const larguraImagem = (dimensoes.larguraUtil - 10) / 2;
    const alturaImagem = 40;

    for (let i = 0; i < imagensValidas.length; i++) {
      // Verificar se precisa de nova página
      if (posY + alturaImagem + 30 > dimensoes.alturaPagina - dimensoes.margem) {
        pdf.addPage();
        posY = dimensoes.margem;
      }

      const coluna = i % 2;
      const x = dimensoes.margem + (coluna * (larguraImagem + 10));

      try {
        // Adicionar imagem (já está em base64)
        const matches = imagensValidas[i].url.match(/^data:(.+);base64,(.+)$/);
        if (matches && matches.length === 3) {
          const mimeType = matches[1];
          const format = mimeType.includes('png') ? 'PNG' : 'JPEG';

          pdf.addImage(imagensValidas[i].url, format, x, posY, larguraImagem, alturaImagem);

          // Adicionar borda à imagem
          pdf.setDrawColor(paleta.borda);
          pdf.setLineWidth(0.2);
          pdf.rect(x, posY, larguraImagem, alturaImagem);

          // Adicionar legenda
          pdf.setFontSize(8);
          pdf.setTextColor(paleta.secundaria);
          const titulo = garantirString(imagensValidas[i].nome);
          const tituloQuebrado = pdf.splitTextToSize(titulo, larguraImagem - 4);

          let alturaTexto = 0;
          if (Array.isArray(tituloQuebrado)) {
            tituloQuebrado.forEach((linha: string, index: number) => {
              pdf.text(linha, x + 2, posY + alturaImagem + 6 + (index * 3));
            });
            alturaTexto = tituloQuebrado.length * 3 + 2;
          } else {
            pdf.text(tituloQuebrado, x + 2, posY + alturaImagem + 6);
            alturaTexto = 4;
          }

          // Avançar posY se for a segunda coluna ou última imagem na primeira coluna
          if (coluna === 1) {
            posY += alturaImagem + 10 + alturaTexto;
          }

          if (i === imagensValidas.length - 1 && coluna === 0) {
            posY += alturaImagem + 10 + alturaTexto;
          }
        }
      } catch (error) {
        console.error(`Erro ao adicionar imagem ao PDF:`, error);
        // Fallback para imagem não carregada
        pdf.setFillColor(240, 240, 240);
        pdf.rect(x, posY, larguraImagem, alturaImagem, 'F');
        pdf.setDrawColor(paleta.borda);
        pdf.rect(x, posY, larguraImagem, alturaImagem);
        pdf.setFontSize(8);
        pdf.setTextColor(paleta.secundaria);
        pdf.text('Imagem não disponível', x + 5, posY + alturaImagem / 2);
      }
    }

    return posY + 5;
  };

  const gerarConteudoPDF = async (
    pdf: jsPDF,
    logoData: string | null,
    imagensCarregadasData: any[]
  ) => {
    const paleta = {
      primaria: "#1A202C",
      secundaria: "#4A5568",
      destaque: "#1F467E",
      borda: "#E2E8F0",
      fundoTabela: "#F7FAFC",
    };

    const dimensoes = {
      larguraPagina: pdf.internal.pageSize.width,
      alturaPagina: pdf.internal.pageSize.height,
      margem: 15,
      larguraUtil: pdf.internal.pageSize.width - (15 * 2)
    };

    // Adicionar logo no cabeçalho
    if (logoData) {
      try {
        if (/^data:(.+);base64,(.+)$/.test(logoData)) {
          adicionarLogo(pdf, logoData, {
            x: dimensoes.larguraPagina - 55,
            y: dimensoes.margem,
            largura: 40,
            altura: 25,
            alinhar: "direita",
          });
        }
      } catch (error) {
        console.error("Erro ao adicionar logo ao PDF:", error);
      }
    }

    let posY = dimensoes.margem + 30;

    // Título principal
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(20);
    pdf.setTextColor(paleta.primaria);
    pdf.text("ORDEM DE SERVIÇO", dimensoes.margem, posY);
    posY += 12;

    // Linha decorativa
    pdf.setDrawColor(paleta.destaque);
    pdf.setLineWidth(0.5);
    pdf.line(dimensoes.margem, posY, dimensoes.larguraPagina - dimensoes.margem, posY);
    posY += 15;

    // Data de realização
    pdf.setFontSize(12);
    pdf.setTextColor(paleta.secundaria);

    const dataRealizado = manutencao.dataRealizado ? formatDate(garantirString(manutencao.dataRealizado)) : 'N/A';
    pdf.text(`Previsão de início: ${dataRealizado}`, dimensoes.margem, posY);
    posY += 12;

    // Informações do Veículo
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(paleta.primaria);
    pdf.text("INFORMAÇÕES DO VEÍCULO", dimensoes.margem, posY);
    posY += 8;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);

    const infoVeiculo = [
      {
        label: "Placa:",
        value: garantirString(manutencao.veiculo?.placa)
      },
      {
        label: "Marca/Modelo:",
        value: `${garantirString(manutencao.veiculo?.marca)} ${garantirString(manutencao.veiculo?.modelo)}`
      },
      {
        label: "Ano:",
        value: garantirString(manutencao.veiculo?.ano)
      },
      {
        label: "Hodômetro:",
        value: `${garantirString(manutencao.hodometro)} km`
      },
    ];

    infoVeiculo.forEach((info) => {
      pdf.text(garantirString(info.label), dimensoes.margem, posY);
      pdf.text(garantirString(info.value), dimensoes.margem + 35, posY);
      posY += 5;
    });
    posY += 8;

    // Prestador de Serviço
    if (manutencao.prestadorServico) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text("PRESTADOR DE SERVIÇO", dimensoes.margem, posY);
      posY += 6;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.text(`Nome: ${garantirString(manutencao.prestadorServico.nome)}`, dimensoes.margem, posY);
      posY += 5;
      if (manutencao.prestadorServico.contato) {
        pdf.text(`Contato: ${garantirString(manutencao.prestadorServico.contato)}`, dimensoes.margem, posY);
        posY += 5;
      }
      posY += 5;
    }

    // Serviços Realizados
    if (manutencao.servicoVeiculoServicos && manutencao.servicoVeiculoServicos.length > 0) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.setTextColor(paleta.primaria);
      pdf.text("SERVIÇOS REALIZADOS", dimensoes.margem, posY);
      posY += 8;

      // Cabeçalho da tabela
      const alturaCabecalho = 8;
      pdf.setFillColor(paleta.fundoTabela);
      pdf.rect(dimensoes.margem, posY - 4, dimensoes.larguraUtil, alturaCabecalho, 'F');

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(paleta.primaria);
      pdf.text("#", dimensoes.margem + 2, posY);
      pdf.text("Descrição do Serviço", dimensoes.margem + 10, posY);

      // Bordas do cabeçalho
      pdf.setDrawColor(paleta.borda);
      pdf.setLineWidth(0.2);
      pdf.rect(dimensoes.margem, posY - 4, dimensoes.larguraUtil, alturaCabecalho);

      posY += alturaCabecalho;

      // Linhas de serviços
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(paleta.secundaria);

      let linhaIndex = 0;
      for (const servico of manutencao.servicoVeiculoServicos) {
        const descricao = garantirString(servico.servico?.nome || servico.nome || servico.descricao || "Serviço não especificado");
        const textoQuebrado = pdf.splitTextToSize(descricao, dimensoes.larguraUtil - 15);
        const linhas = Array.isArray(textoQuebrado) ? textoQuebrado : [textoQuebrado];
        const alturaLinha = Math.max(6, linhas.length * 4 + 2);

        // Verificar se precisa de nova página
        if (posY + alturaLinha > dimensoes.alturaPagina - dimensoes.margem) {
          pdf.addPage();
          posY = dimensoes.margem;
          linhaIndex = 0;
        }

        // Fundo alternado para melhor legibilidade
        if (linhaIndex % 2 === 0) {
          pdf.setFillColor(250, 250, 250);
          pdf.rect(dimensoes.margem, posY - 2, dimensoes.larguraUtil, alturaLinha, 'F');
        }

        // Número do serviço
        pdf.text(`${linhaIndex + 1}`, dimensoes.margem + 2, posY + 2);

        // Descrição do serviço (multilinha)
        linhas.forEach((linha: string, index: number) => {
          pdf.text(linha, dimensoes.margem + 10, posY + 2 + (index * 4));
        });

        // Bordas da linha
        pdf.setDrawColor(paleta.borda);
        pdf.setLineWidth(0.1);
        pdf.line(dimensoes.margem, posY - 2, dimensoes.margem + dimensoes.larguraUtil, posY - 2);
        pdf.line(dimensoes.margem, posY + alturaLinha - 2, dimensoes.margem + dimensoes.larguraUtil, posY + alturaLinha - 2);
        pdf.line(dimensoes.margem, posY - 2, dimensoes.margem, posY + alturaLinha - 2);
        pdf.line(dimensoes.margem + dimensoes.larguraUtil, posY - 2, dimensoes.margem + dimensoes.larguraUtil, posY + alturaLinha - 2);

        posY += alturaLinha;
        linhaIndex++;
      }

      posY += 5;
    }

    // Peças Utilizadas
    if (manutencao.servicoVeiculoPecas && manutencao.servicoVeiculoPecas.length > 0) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.setTextColor(paleta.primaria);
      pdf.text("PEÇAS UTILIZADAS", dimensoes.margem, posY);
      posY += 8;

      // Cabeçalho da tabela
      const alturaCabecalho = 8;
      pdf.setFillColor(paleta.fundoTabela);
      pdf.rect(dimensoes.margem, posY - 4, dimensoes.larguraUtil, alturaCabecalho, 'F');

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(paleta.primaria);
      pdf.text("#", dimensoes.margem + 2, posY);
      pdf.text("Descrição", dimensoes.margem + 10, posY);
      pdf.text("Marca/Modelo", dimensoes.margem + 70, posY);
      pdf.text("Qtd", dimensoes.margem + dimensoes.larguraUtil - 12, posY);

      // Bordas do cabeçalho
      pdf.setDrawColor(paleta.borda);
      pdf.setLineWidth(0.2);
      pdf.rect(dimensoes.margem, posY - 4, dimensoes.larguraUtil, alturaCabecalho);

      posY += alturaCabecalho;

      // Linhas de peças
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(paleta.secundaria);

      let linhaIndex = 0;
      for (const peca of manutencao.servicoVeiculoPecas) {
        const nomePeca = garantirString(peca.peca?.nome || peca.nome || "Peça não especificada");
        const marca = garantirString(peca.peca?.marca || peca.marca || "");
        const modelo = garantirString(peca.peca?.modelo || peca.modelo || "");
        const qtd = peca.quantidadeUsada || peca.quantidade || 1;

        const descricaoQuebrada = pdf.splitTextToSize(nomePeca, 55);
        const marcaModeloQuebrada = pdf.splitTextToSize(`${marca} ${modelo}`, 45);

        const linhasDescricao = Array.isArray(descricaoQuebrada) ? descricaoQuebrada : [descricaoQuebrada];
        const linhasMarcaModelo = Array.isArray(marcaModeloQuebrada) ? marcaModeloQuebrada : [marcaModeloQuebrada];

        const alturaLinha = Math.max(6, Math.max(linhasDescricao.length, linhasMarcaModelo.length) * 4 + 2);

        // Verificar se precisa de nova página
        if (posY + alturaLinha > dimensoes.alturaPagina - dimensoes.margem) {
          pdf.addPage();
          posY = dimensoes.margem;
          linhaIndex = 0;
        }

        // Fundo alternado
        if (linhaIndex % 2 === 0) {
          pdf.setFillColor(250, 250, 250);
          pdf.rect(dimensoes.margem, posY - 2, dimensoes.larguraUtil, alturaLinha, 'F');
        }

        // Número da peça
        pdf.text(`${linhaIndex + 1}`, dimensoes.margem + 2, posY + 2);

        // Descrição (multilinha)
        linhasDescricao.forEach((linha: string, index: number) => {
          pdf.text(linha, dimensoes.margem + 10, posY + 2 + (index * 4));
        });

        // Marca/Modelo (multilinha)
        linhasMarcaModelo.forEach((linha: string, index: number) => {
          pdf.text(linha, dimensoes.margem + 70, posY + 2 + (index * 4));
        });

        // Quantidade
        pdf.text(String(qtd), dimensoes.margem + dimensoes.larguraUtil - 12, posY + 2);

        // Bordas da linha
        pdf.setDrawColor(paleta.borda);
        pdf.setLineWidth(0.1);
        pdf.line(dimensoes.margem, posY - 2, dimensoes.margem + dimensoes.larguraUtil, posY - 2);
        pdf.line(dimensoes.margem, posY + alturaLinha - 2, dimensoes.margem + dimensoes.larguraUtil, posY + alturaLinha - 2);
        pdf.line(dimensoes.margem, posY - 2, dimensoes.margem, posY + alturaLinha - 2);
        pdf.line(dimensoes.margem + dimensoes.larguraUtil, posY - 2, dimensoes.margem + dimensoes.larguraUtil, posY + alturaLinha - 2);
        pdf.line(dimensoes.margem + 65, posY - 2, dimensoes.margem + 65, posY + alturaLinha - 2);
        pdf.line(dimensoes.margem + dimensoes.larguraUtil - 15, posY - 2, dimensoes.margem + dimensoes.larguraUtil - 15, posY + alturaLinha - 2);

        posY += alturaLinha;
        linhaIndex++;
      }

      posY += 5;
    }

    // Observações
    if (manutencao.descricao && manutencao.descricao.trim() !== '') {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text("OBSERVAÇÕES", dimensoes.margem, posY);
      posY += 6;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      const observacoes = pdf.splitTextToSize(garantirString(manutencao.descricao), dimensoes.larguraUtil);
      if (Array.isArray(observacoes)) {
        observacoes.forEach((linha: string) => {
          if (posY + 4 > dimensoes.alturaPagina - dimensoes.margem) {
            pdf.addPage();
            posY = dimensoes.margem;
          }
          pdf.text(linha, dimensoes.margem, posY);
          posY += 4;
        });
      } else {
        if (posY + 4 > dimensoes.alturaPagina - dimensoes.margem) {
          pdf.addPage();
          posY = dimensoes.margem;
        }
        pdf.text(observacoes, dimensoes.margem, posY);
        posY += 4;
      }
      posY += 6;
    }

    // Adicionar imagens se disponíveis
    posY = await adicionarImagensAoPDF(pdf, posY, dimensoes, paleta, imagensCarregadasData);

    // Rodapé
    const rodapeY = dimensoes.alturaPagina - 20;
    pdf.setFontSize(8);
    pdf.setTextColor(paleta.secundaria);
    pdf.text("Este relatório foi gerado automaticamente pelo sistema LocaSmartPro", dimensoes.margem, rodapeY);
    pdf.text(`Emitido em: ${new Date().toLocaleString('pt-BR')}`, dimensoes.larguraPagina - dimensoes.margem - 40, rodapeY);

    // Linha "Emitido por"
    pdf.setFontSize(8);
    pdf.setTextColor(paleta.primaria);
    pdf.text(`Emitido por: ${obterNomeUsuario(token)}`, dimensoes.margem, rodapeY + 5);
  };

  const gerarPDFManutencao = async () => {
    if (!manutencao) {
      console.error("Dados insuficientes para gerar PDF");
      toast.error("Dados insuficientes para gerar o relatório");
      return;
    }

    // Bloquear múltiplas execuções usando ref
    if (pdfEmExecucaoRef.current) {
      return;
    }

    pdfEmExecucaoRef.current = true;
    setGerandoPDF(true);
    setCarregando(true);

    try {
      // 1. CARREGAR LOGO
      const logoData = await carregarLogoBase64();

      // 2. CARREGAR IMAGENS
      const imagensCarregadas = await carregarImagens();

      // 3. CRIAR PDF
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      
      // 4. GERAR CONTEÚDO passando dados carregados
      await gerarConteudoPDF(pdf, logoData, imagensCarregadas);

      // 5. ABRIR PDF
      const pdfBlob = pdf.output("blob");
      const url = URL.createObjectURL(pdfBlob);
      const id = `os_${manutencao.id || Date.now()}`;
      localStorage.setItem(`pdfURL_${id}`, url);
      
      // Usar setTimeout para evitar bloqueio de popup
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        window.open(`/pdf-viewer?id=${id}`, "_blank");
      }, 100);
      
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      toast.error("Erro ao gerar o PDF");
    } finally {
      // Limpar estados após um delay para evitar múltiplas execuções rápidas
      setTimeout(() => {
        pdfEmExecucaoRef.current = false;
        setGerandoPDF(false);
        setCarregando(false);
      }, 500);
    }
  };

  useEffect(() => {
    // Verificar se o trigger mudou e se não estamos já gerando um PDF
    if (trigger > 0 && trigger !== ultimoTriggerRef.current && manutencao && !pdfEmExecucaoRef.current) {
      ultimoTriggerRef.current = trigger;
      gerarPDFManutencao();
    }
  }, [trigger, manutencao]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (carregando || gerandoPDF) {
    return (
      <div className="flex justify-center items-center p-4">
        <div className="text-blue-600">
          {carregando ? "Carregando dados..." : "Gerando PDF..."}
        </div>
      </div>
    );
  }

  return null;
};